/**
 * 待办事项离线操作队列
 * 仅当写 Supabase 失败时入队，网络恢复后后台重试并做时间/内容比对合并
 */

import type { Todo } from '@ext-bookmark/shared/types';

const STORAGE_KEY = 'todo_offline_queue';
const MAX_RETRIES = 5;
const MAX_QUEUE_LENGTH = 100;

export type TodoQueuedOpType = 'SAVE_TODO' | 'TOGGLE_TODO' | 'DELETE_TODO' | 'DELETE_TODOS';

export interface TodoQueuedOp {
  id: string;
  type: TodoQueuedOpType;
  payload: Todo | { todoId: string; instanceDate?: number } | { todoIds: string[] };
  timestamp: number;
  retryCount: number;
  createdAt: number;
}

function loadQueue(): TodoQueuedOp[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveQueue(queue: TodoQueuedOp[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[TodoOfflineQueue] 保存队列失败:', e);
  }
}

/**
 * 入队：写 Supabase 失败时调用
 */
export function enqueueTodoOp(
  type: TodoQueuedOpType,
  payload: Todo | { todoId: string; instanceDate?: number } | { todoIds: string[] }
): void {
  const queue = loadQueue();
  if (queue.length >= MAX_QUEUE_LENGTH) {
    queue.shift();
  }
  const op: TodoQueuedOp = {
    id: `todo_op_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    type,
    payload,
    timestamp: Date.now(),
    retryCount: 0,
    createdAt: Date.now(),
  };
  queue.push(op);
  saveQueue(queue);
}

/**
 * 获取队列（按 timestamp 排序）
 */
export function getTodoQueue(): TodoQueuedOp[] {
  const queue = loadQueue();
  return [...queue].sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * 移除指定操作
 */
export function removeTodoOp(opId: string): void {
  const queue = loadQueue().filter((op) => op.id !== opId);
  saveQueue(queue);
}

/**
 * 增加重试计数并更新队列
 */
export function incrementTodoOpRetry(opId: string): TodoQueuedOp | null {
  const queue = loadQueue();
  const idx = queue.findIndex((op) => op.id === opId);
  if (idx < 0) return null;
  queue[idx].retryCount += 1;
  saveQueue(queue);
  return queue[idx];
}

/**
 * 是否超过最大重试次数
 */
export function isTodoOpExhausted(op: TodoQueuedOp): boolean {
  return op.retryCount >= MAX_RETRIES;
}

/**
 * 清空队列
 */
export function clearTodoQueue(): void {
  saveQueue([]);
}
